import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React from 'react';
import _ from 'underscore';

import * as RegistryConstants from 'constants/RegistryConstants';
import {BatchLinkProps} from 'constants/SampleConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import notifications from 'notifications';
import FieldTable, {ClientSchemaField, SaveAction} from 'registry/field/FieldTable';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {AllStorableSchemasWithOrganizationQuery} from 'schemas/schema-interface/__generated/AllStorableSchemasWithOrganizationQuery';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import {filterSchemasByRegistryOrganization} from 'schemas/util/FilterSchemasByOrganization';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {capwords} from 'util/NameUtil';
import {MakeFieldsOptional} from 'util/Types';

import {OptionalBaseBioentityType} from './SchemaInterfaceBaseBioentityTypeDropdown';
import SchemaInterfaceFieldTableRow from './SchemaInterfaceFieldTableRow';

export const ALL_STORABLE_SCHEMAS_WITH_ORGANIZATION_QUERY: DocumentNode = gql`
  query AllStorableSchemasWithOrganizationQuery @skipAudit @engComponent(name: SCHEMAS) {
    boxSchemas {
      ...BoxSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    containerSchemas {
      ...ContainerSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    locationSchemas {
      ...LocationSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    plateSchemas {
      ...PlateSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
  }
  ${getFragmentWrapper('BoxSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
  ${getFragmentWrapper('LocationSchema').fragment}
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('Registry').fragment}
`;

export type ClientInterfaceField = MakeFieldsOptional<SchemaFieldConstants.InterfaceField, 'id' | 'apiId'>;

export const convertFromClientInterfaceFieldToSchemaField = (
  interfaceField: ClientInterfaceField
): ClientSchemaField => ({
  ...interfaceField,
  name: interfaceField.displayName,
  dbId: interfaceField.id,
  archiveRecord: null,
  cid: _.uniqueId(),
  isComputed: false,
  isConvertedFromLink: false,
  isSnapshot: false,
  isIntegration: false,
  isParentLink: false,
});

const convertFromSchemaFieldToClientInterfaceField = (
  clientSchemaField: ClientSchemaField
): ClientInterfaceField => ({
  id: clientSchemaField.id,
  apiId: clientSchemaField.apiId,
  systemName: clientSchemaField.systemName,
  displayName: clientSchemaField.displayName,
  fieldType: clientSchemaField.fieldType,
  isRequired: clientSchemaField.isRequired,
  isMulti: clientSchemaField.isMulti,
  requiredLink: clientSchemaField.requiredLink || null,
  schemaFieldSelectorId: clientSchemaField.schemaFieldSelectorId ?? null,
});

export interface BaseSchemaInterfaceFieldTableProps {
  batchLinkProps?: BatchLinkProps;
  ownerId: string;
  ownerUrlId: string;
  fields: Array<ClientInterfaceField>;
  isDisabled: boolean;
  savingState: RegistryConstants.SavingState;
  handleDeleteConfirmation?: (onSubmit: (param?: string) => void) => void;
  subheader?: JSX.Element;
  dataTestAttrs?: DataTestAttrs;
  baseBioentityType?: OptionalBaseBioentityType;
}

export interface SchemaInterfaceFieldTableProps extends BaseSchemaInterfaceFieldTableProps {
  onFieldsChange?: (clientInterfaceFields: Array<ClientInterfaceField>) => void;
  onFieldSave?: (field: ClientInterfaceField, saveAction: SaveAction) => void;
  isBeingCreated: boolean;
  submissionWarnings: Array<JSX.Element>;
}

function SchemaInterfaceFieldTable(props: SchemaInterfaceFieldTableProps): JSX.Element {
  const allStorableSchemasResult = useBenchlingQuery<AllStorableSchemasWithOrganizationQuery>(
    ALL_STORABLE_SCHEMAS_WITH_ORGANIZATION_QUERY
  );

  if (allStorableSchemasResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (allStorableSchemasResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load schema interface; try reloading the page.');
    return <></>;
  }

  const allStorableSchemas = [
    ...allStorableSchemasResult.data.containerSchemas,
    ...allStorableSchemasResult.data.boxSchemas,
    ...allStorableSchemasResult.data.locationSchemas,
    ...allStorableSchemasResult.data.plateSchemas,
  ];

  const filteredStorableSchemas = filterSchemasByRegistryOrganization(allStorableSchemas, props.ownerId);

  const storableSchemas = graphQLArrayToREST('StorableSchema', filteredStorableSchemas);

  const onFieldsChange = (schemaFields: Array<ClientSchemaField>): void => {
    if (props.onFieldsChange) {
      props.onFieldsChange(schemaFields.map((field) => convertFromSchemaFieldToClientInterfaceField(field)));
    }
  };

  const onFieldSave = (field: ClientSchemaField, saveAction: SaveAction): void => {
    if (props.onFieldSave) {
      props.onFieldSave(convertFromSchemaFieldToClientInterfaceField(field), saveAction);
    }
  };

  return (
    <FieldTable
      {...props}
      fields={props.fields.map((field) => convertFromClientInterfaceFieldToSchemaField(field))}
      onFieldsChange={props.onFieldsChange ? onFieldsChange : undefined}
      onFieldSave={props.onFieldSave ? onFieldSave : undefined}
      shouldShowIsIntegration={false}
      shouldShowIsParentLink={false}
      shouldShowIsRequired={true}
      itemClass={SchemaInterfaceFieldTableRow}
      schemaType={capwords(schemaFieldsetName())}
      nonSchemaNameOverride={schemaFieldsetName()}
      fileType={props.baseBioentityType?.folderItemType}
      sequenceType={props.baseBioentityType?.sequenceType}
      storableSchemas={storableSchemas}
    />
  );
}

export default SchemaInterfaceFieldTable;
